export interface HreflangTag {
  href: string
  hreflang: string
}

export interface OpenGraphMetadata {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: string
}

export interface TwitterMetadata {
  card?: 'summary' | 'summary_large_image'
  title?: string
  description?: string
  image?: string
  creator?: string
}

export interface SEOData {
  title?: string
  description?: string
  keywords?: string
  canonicalUrl?: string
  ogImage?: string
  hreflangTags?: HreflangTag[]
  robots?: string
  openGraph?: OpenGraphMetadata
  twitter?: TwitterMetadata
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>
}

export const SITE_URL = 'https://zypheron.net'
export const SITE_NAME = 'ZYPHERON'
export const SITE_TITLE_SUFFIX = ' | ZYPHERON'
// TODO: Replace this placeholder with /zypheron-og.png, a proper 1200x630
// branded social card. Zypheron-logo.png is a square brand mark, not an ideal
// OG card, but it is a real raster that exists in public/ and keeps previews valid.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/Zypheron-logo.png`
export const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'

export const normalizeTitle = (title?: string): string | undefined => {
  if (!title) return undefined
  // Case-INSENSITIVE check so titles already ending in a Zypheron suffix
  // (e.g. "... | Zypheron Blog") don't get "| ZYPHERON" double-appended.
  if (title.toLowerCase().includes(SITE_NAME.toLowerCase())) return title
  return `${title}${SITE_TITLE_SUFFIX}`
}

// Any ogImage that ends in .svg is invalid for OG/Twitter cards. Fall back to
// the default raster PNG in the shared SEO layer.
export const resolveOgImage = (image?: string): string => {
  if (!image || image.toLowerCase().endsWith('.svg')) return DEFAULT_OG_IMAGE
  return image
}

export const toAbsoluteUrl = (value?: string): string | undefined => {
  if (!value) return value
  if (value.startsWith('http://') || value.startsWith('https://')) return value
  return `${SITE_URL}${value.startsWith('/') ? value : `/${value}`}`
}

// Shared Organization node, reused by WebSite/publisher and the global schema.
export const ORGANIZATION_SCHEMA: Record<string, unknown> = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/Zypheron-logo.png`,
  sameAs: [
    'https://github.com/KKingZero/Zypheron-CLI',
    'https://twitter.com/zypheronsec',
  ],
}

// Site-wide WebSite node. No site search exists, so SearchAction is omitted.
export const WEBSITE_SCHEMA: Record<string, unknown> = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
}

export const PRODUCT_POSITIONING =
  'Cybersecurity IDE for security teams and pentesting firms: AI-assisted assessment workflows, evidence collection, local-first analysis, and board/client-ready reporting.'

/**
 * Build BlogPosting + BreadcrumbList JSON-LD for a /blog/:slug route from the
 * shared blogPosts registry. Returns null when the pathname is not a blog post.
 * Used by both the client (SEOOptimizer) and SSR (entry-server) paths.
 */
export const buildBlogStructuredData = (
  pathname: string,
  posts: ReadonlyArray<{ title: string; excerpt: string; author: string; date: string; slug: string }>,
): Array<Record<string, unknown>> | null => {
  if (!pathname.startsWith('/blog/')) return null
  const slug = pathname.replace(/^\/blog\//, '').replace(/\/$/, '')
  const post = posts.find((p) => p.slug === slug)
  if (!post) return null

  const canonical = `${SITE_URL}/blog/${post.slug}`

  const blogPosting: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    author: { '@type': 'Person', name: post.author },
    datePublished: post.date,
    dateModified: post.date,
    image: DEFAULT_OG_IMAGE,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    url: canonical,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/Zypheron-logo.png` },
    },
  }

  const breadcrumb: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
      { '@type': 'ListItem', position: 3, name: post.title, item: canonical },
    ],
  }

  return [blogPosting, breadcrumb]
}
